import React, { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import {
  Alert,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  IconButton,
  CircularProgress,
  Skeleton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { supabase } from '../lib/supabase';
import {
  getSpeakerName,
  updateSpeakerName,
  normaliseTranscript,
  stripHtmlToText,
} from './sessions/transcriptUtils';

const ITEMS_PER_PAGE = 50;

/**
 * A single editable utterance for structured (Fathom/Recall) transcripts.
 * @param {object} props
 * @param {number} props.index - Index in the full transcript array.
 * @param {object} props.utterance - Utterance data.
 * @param {Function} props.onSpeakerChange - (index, newName) => void
 * @param {Function} props.onTextChange - (index, newText) => void
 * @returns {JSX.Element}
 */
const UtteranceBlock = memo(({ index, utterance, onSpeakerChange, onTextChange }) => (
  <Box
    sx={{
      p: 1.5,
      mb: 1,
      border: 1,
      borderColor: 'divider',
      borderRadius: 'var(--q-radius-lg)',
      bgcolor: 'background.paper',
      '&:last-child': { mb: 0 },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
      <TextField
        size="small"
        label="Speaker"
        value={getSpeakerName(utterance)}
        onChange={(e) => onSpeakerChange(index, e.target.value)}
        sx={{ width: { xs: '100%', sm: 220 } }}
      />
      {utterance.timestamp && (
        <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {utterance.timestamp}
        </Typography>
      )}
    </Box>
    <TextField
      fullWidth
      multiline
      placeholder="Transcript text..."
      size="small"
      value={utterance.text || utterance.content || ''}
      onChange={(e) => onTextChange(index, e.target.value)}
      inputProps={{ 'aria-label': `Line ${index + 1} text` }}
    />
  </Box>
), (prev, next) =>
  prev.index === next.index &&
  prev.utterance === next.utterance &&
  prev.onSpeakerChange === next.onSpeakerChange &&
  prev.onTextChange === next.onTextChange
);

UtteranceBlock.displayName = 'UtteranceBlock';

/**
 * Edit a session's name, description and transcript. Structured (Fathom /
 * Recall utterance array) transcripts are edited line by line with a speaker
 * filter and paging; other transcripts are edited as plain text.
 *
 * @param {object} props
 * @param {boolean} props.open - Whether the dialog is open.
 * @param {Function} props.onClose - Callback when dialog is closed without saving.
 * @param {object} props.session - Session (transcript optional — lazy-loaded here when missing).
 * @param {object} [props.project] - Parent project object.
 * @param {Function} props.onSave - async (updates: {name, description, transcript}) => {success, error?}
 * @param {boolean} [props.saving] - Externally controlled saving state.
 * @returns {JSX.Element}
 */
function SessionEditorDialog({ open, onClose, session, onSave, saving: externalSaving }) {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const [formData, setFormData] = useState({ name: '', description: '', content: '' });
  const [baseTranscript, setBaseTranscript] = useState(null);
  const [isStructuredEdit, setIsStructuredEdit] = useState(false);
  const [structuredTranscript, setStructuredTranscript] = useState([]);
  const [speakerFilter, setSpeakerFilter] = useState('All');
  const [page, setPage] = useState(1);
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const [internalSaving, setInternalSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const saving = Boolean(externalSaving || internalSaving);

  // Latest session object, read by the initialiser below. Parents often rebuild the session
  // object on every render, so the initialiser keys on the id instead of the object identity;
  // otherwise any parent re-render would reset the user's edits.
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const sessionId = session?.id ?? null;

  // Initialise the form and lazy-load the transcript when the dialog opens or switches session.
  useEffect(() => {
    const current = sessionRef.current;
    if (!open || !current) return undefined;
    let cancelled = false;

    setFormData({ name: current.name || '', description: current.description || '', content: '' });
    setBaseTranscript(null);
    setIsStructuredEdit(false);
    setStructuredTranscript([]);
    setSpeakerFilter('All');
    setPage(1);
    setSaveError('');

    async function loadTranscript() {
      let raw = current.transcript;
      if (!raw && current.id) {
        setLoadingTranscript(true);
        try {
          const { data, error } = await supabase
            .from('sessions')
            .select('transcript')
            .eq('id', current.id)
            .single();
          if (!error) raw = data?.transcript;
        } catch {
          // continue without transcript
        } finally {
          if (!cancelled) setLoadingTranscript(false);
        }
      }

      if (cancelled) return;
      const parsed = normaliseTranscript(raw);
      setBaseTranscript(parsed);
      if (!parsed) return;

      const hasStructured =
        (current.source === 'fathom' || current.source === 'recall') &&
        Array.isArray(parsed.transcript);

      if (hasStructured) {
        setIsStructuredEdit(true);
        setStructuredTranscript(JSON.parse(JSON.stringify(parsed.transcript)));
      } else {
        setIsStructuredEdit(false);
        setFormData((prev) => ({ ...prev, content: stripHtmlToText(parsed.content || '') }));
      }
    }

    loadTranscript();
    return () => { cancelled = true; };
  }, [open, sessionId]);

  const handleSpeakerChange = useCallback((idx, val) => {
    setStructuredTranscript((prev) => {
      const updated = [...prev];
      updated[idx] = updateSpeakerName(updated[idx], val);
      return updated;
    });
  }, []);

  const handleTextChange = useCallback((idx, val) => {
    setStructuredTranscript((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], text: val, content: val };
      return updated;
    });
  }, []);

  /**
   * Build the updated transcript object and call onSave. The loaded
   * transcript is the base so its metadata (source, file info) is kept.
   */
  const handleSave = useCallback(async () => {
    if (!session) return;
    setInternalSaving(true);
    setSaveError('');

    const existingTranscript = baseTranscript || (session.transcript && typeof session.transcript === 'object' ? session.transcript : {});
    const updatedTranscript = { ...existingTranscript, editedAt: new Date().toISOString() };

    if (isStructuredEdit) {
      // Only the utterance array is stored; the backend derives text from it.
      updatedTranscript.transcript = structuredTranscript;
      delete updatedTranscript.content;
    } else {
      updatedTranscript.content = formData.content;
      updatedTranscript.transcript = null; // the viewer uses content
    }

    try {
      const result = await onSave({
        name: formData.name,
        description: formData.description,
        transcript: updatedTranscript,
      });
      if (result?.success === false) throw new Error(result.error || 'Save failed');
    } catch (err) {
      console.error('SessionEditorDialog save error:', err);
      setSaveError(err.message || 'Could not save the session.');
    } finally {
      setInternalSaving(false);
    }
  }, [session, baseTranscript, formData, isStructuredEdit, structuredTranscript, onSave]);

  const uniqueSpeakers = useMemo(
    () => [...new Set(structuredTranscript.map((u) => getSpeakerName(u)))].sort(),
    [structuredTranscript]
  );

  /** Utterances visible under the speaker filter, with their real indexes. */
  const visibleUtterances = useMemo(() => {
    const all = structuredTranscript.map((utterance, index) => ({ utterance, index }));
    return speakerFilter === 'All' ? all : all.filter(({ utterance }) => getSpeakerName(utterance) === speakerFilter);
  }, [structuredTranscript, speakerFilter]);

  const totalPages = Math.max(1, Math.ceil(visibleUtterances.length / ITEMS_PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = visibleUtterances.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose()}
      maxWidth="md"
      fullWidth
      fullScreen={isPhone}
      disableRestoreFocus
    >
      <DialogTitle>Edit session</DialogTitle>
      <DialogContent>
        {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
        <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
          <TextField
            autoFocus
            label="Session name"
            fullWidth
            value={formData.name}
            onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={2}
            value={formData.description}
            onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1, mt: 3 }}>
          <Typography variant="subtitle2">Transcript</Typography>
          {isStructuredEdit && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="speaker-filter-label">Speaker</InputLabel>
              <Select
                labelId="speaker-filter-label"
                value={speakerFilter}
                label="Speaker"
                onChange={(e) => {
                  setSpeakerFilter(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="All">All speakers</MenuItem>
                {uniqueSpeakers.map((name) => (
                  <MenuItem key={name} value={name}>{name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        {loadingTranscript ? (
          <Skeleton variant="rounded" height={320} sx={{ borderRadius: 'var(--q-radius-xl)' }} />
        ) : (
          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 'var(--q-radius-xl)',
              overflow: 'hidden',
              height: { xs: 'auto', sm: 450 },
              minHeight: 280,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {isStructuredEdit ? (
              <>
                <Box sx={{ p: 1.5, overflowY: 'auto', flex: 1, bgcolor: 'background.default' }}>
                  {pageItems.map(({ utterance, index }) => (
                    <UtteranceBlock
                      key={index}
                      index={index}
                      utterance={utterance}
                      onSpeakerChange={handleSpeakerChange}
                      onTextChange={handleTextChange}
                    />
                  ))}
                  {!pageItems.length && (
                    <Typography variant="body2" color="text.secondary">No lines for this speaker.</Typography>
                  )}
                </Box>
                {visibleUtterances.length > ITEMS_PER_PAGE && (
                  <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, bgcolor: 'background.paper' }}>
                    <IconButton
                      size="small"
                      aria-label="Previous page"
                      onClick={() => setPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft fontSize="small" />
                    </IconButton>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <Select
                        value={currentPage}
                        onChange={(e) => setPage(Number(e.target.value))}
                        inputProps={{ 'aria-label': 'Transcript page' }}
                      >
                        {Array.from({ length: totalPages }).map((_, i) => {
                          const start = visibleUtterances[i * ITEMS_PER_PAGE]?.utterance;
                          const end = visibleUtterances[Math.min((i + 1) * ITEMS_PER_PAGE, visibleUtterances.length) - 1]?.utterance;
                          return (
                            <MenuItem key={i + 1} value={i + 1}>
                              Page {i + 1} ({start?.timestamp || '00:00'} - {end?.timestamp || 'End'})
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                    <IconButton
                      size="small"
                      aria-label="Next page"
                      onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </>
            ) : (
              <TextField
                fullWidth
                multiline
                minRows={14}
                value={formData.content}
                onChange={(e) => setFormData((prev) => ({ ...prev, content: e.target.value }))}
                placeholder="Session transcript content..."
                inputProps={{ 'aria-label': 'Transcript text' }}
                sx={{
                  flex: 1,
                  '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start', p: 2 },
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '& textarea': { overflow: 'auto !important' },
                }}
              />
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || loadingTranscript || !formData.name.trim()}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {saving ? 'Saving...' : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SessionEditorDialog;
